import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  UserEntersBirthDay,
  userEntersBirthDay,
  UserEntersBirthMonth,
  userEntersBirthMonth,
  userEntersBirthYear,
  UserEntersBirthYear,
} from '../actions/Actions';
import { IProductInfo, IState, IUserInfo } from '../CustomTypes';
import { validate18YearsOld } from '../validators';
import { ClientShortHandName } from '@snoam/mono-scc';
import {
  classNames,
  Display,
  FontSize,
  Icon,
  IconColor,
  IconName,
  IconSize,
  Input,
  Margin,
  Padding,
  TextColor,
  Width,
} from '@snoam/pinata';
import moment from 'moment';
import { lowerAgeLimitYears } from '../fastflowUtils';

export interface IActionProps {
  userEntersBirthDay: UserEntersBirthDay;
  userEntersBirthMonth: UserEntersBirthMonth;
  userEntersBirthYear: UserEntersBirthYear;
}

export interface IParamProps {
  formSubmitted?: boolean;
}

export interface IStateProps {
  productInfo: IProductInfo;
  userInfo: IUserInfo;
}

export interface IProps extends IParamProps, IStateProps, IActionProps {}

const styleClass = {
  error: classNames(
    TextColor.TEXT_NEGATIVE,
    Margin.MT_NEGATIVE_4,
    Margin.MB_4,
    FontSize.TEXT_SM,
  ),
  icon: classNames(Display.INLINE_BLOCK, Margin.ML_2),
  info: classNames(Margin.MT_NEGATIVE_4, Margin.MB_4, FontSize.TEXT_SM),
  input: classNames(Margin.MR_2, Width.W_1_3),
  inputsWrapper: classNames(Display.FLEX, Padding.PT_2),
  label: classNames(
    Display.BLOCK,
    TextColor.TEXT_NEUTRAL_6,
    FontSize.TEXT_SM,
    Margin.MT_2,
  ),
  yearInput: classNames(Width.W_1_3),
};

export class BirthDate extends React.Component<
  IProps,
  {
    isInvalidDate: boolean;
    under18YearsOld: boolean;
    showValidationError: boolean;
    enteredDayLength: number | null;
    enteredMonthLength: number | null;
    enteredYearLength: number | null;
  }
> {
  private monthInput: HTMLDivElement;
  private yearInput: HTMLDivElement;

  constructor(props: IProps) {
    super(props);
    this.onBlur = this.onBlur.bind(this);
    this.birthDateEntered = this.birthDateEntered.bind(this);
    this.isValidDate = this.isValidDate.bind(this);
    this.validate18YearsOld = this.validate18YearsOld.bind(this);
    this.dayEntered = this.dayEntered.bind(this);
    this.monthEntered = this.monthEntered.bind(this);
    this.yearEntered = this.yearEntered.bind(this);
    this.state = {
      enteredDayLength: null,
      enteredMonthLength: null,
      enteredYearLength: null,
      isInvalidDate: false,
      showValidationError: false,
      under18YearsOld: false,
    };
  }

  public render() {
    const { birthDay, birthMonth, birthYear } = this.props.userInfo;
    const { owningClient, pureDigital } = this.props.productInfo;
    const { formSubmitted } = this.props;
    const { enteredDayLength, enteredMonthLength, enteredYearLength } =
      this.state;

    const showValidationError =
      (formSubmitted || this.state.showValidationError) &&
      (!this.isValidDate() || !this.validate18YearsOld());
    const ageDiscountApplies = // eslint-disable-line @typescript-eslint/no-unused-vars
      pureDigital &&
      owningClient !== ClientShortHandName.DP &&
      owningClient !== ClientShortHandName.E24;

    return (
      <>
        <label htmlFor="input_day" className={styleClass.label}>
          Din fødselsdato
          {this.isValidDate() && this.validate18YearsOld() && (
            <Icon
              name={IconName.CHECK}
              size={IconSize.SMALL}
              color={IconColor.POSITIVE}
              className={styleClass.icon}
              screenReaderText={'Ikon som viser at feltet er fylt ut riktig'}
            />
          )}
          {showValidationError && (
            <Icon
              name={IconName.INFO}
              size={IconSize.SMALL}
              color={IconColor.NEGATIVE}
              className={styleClass.icon}
              screenReaderText={
                'Ikon som viser at feltet ikke er fylt ut riktig'
              }
            />
          )}
        </label>

        <div className={styleClass.inputsWrapper}>
          <div className={styleClass.input}>
            <Input
              type="tel"
              inputMode="numeric"
              pattern="\\d{1,2}"
              title="DD"
              min="1"
              max="31"
              placeholder="DD"
              name="day"
              value={birthDay}
              onChange={this.dayEntered}
              aria-label="Dag"
              errorMsg={showValidationError ? ' ' : ''}
              validMsg={enteredDayLength === 2 && !showValidationError}
              clearable={true}
            />
          </div>
          <div
            ref={(inputWrapper) => (this.monthInput = inputWrapper)}
            className={styleClass.input}
          >
            <Input
              type="tel"
              inputMode="numeric"
              pattern="\\d{1,2}"
              title="MM"
              min="1"
              max="12"
              placeholder="MM"
              name="month"
              value={birthMonth}
              onChange={this.monthEntered}
              aria-label="Måned"
              errorMsg={showValidationError ? ' ' : ''}
              validMsg={enteredMonthLength === 2 && !showValidationError}
              clearable={true}
            />
          </div>
          <div
            ref={(inputWrapper) => (this.yearInput = inputWrapper)}
            className={styleClass.yearInput}
          >
            <Input
              type="tel"
              inputMode="numeric"
              pattern="\\d{4}"
              title="ÅÅÅÅ"
              placeholder="ÅÅÅÅ"
              name="year"
              value={birthYear}
              onChange={this.yearEntered}
              onBlur={this.onBlur}
              aria-label="År"
              errorMsg={showValidationError ? ' ' : ''}
              validMsg={enteredYearLength === 4 && !showValidationError}
              clearable={true}
            />
          </div>
        </div>
        {showValidationError && (
          <div className={styleClass.error}>
            {!this.birthDateEntered()
              ? 'Vennligst oppgi fødselsdato på formatet DD MM ÅÅÅÅ'
              : this.isValidDate() && !this.validate18YearsOld()
                ? `Du må være over ${lowerAgeLimitYears} år for å bestille abonnement.`
                : 'Vennligst oppgi en gyldig fødselsdato'}
          </div>
        )}
      </>
    );
  }

  private onBlur() {
    const under18YearsOld = !this.validate18YearsOld();
    const isInvalidDate = !this.isValidDate();
    this.setState({
      isInvalidDate,
      showValidationError: isInvalidDate || under18YearsOld,
      under18YearsOld,
    });
  }

  private isValidDate(): boolean {
    const { birthDay, birthMonth, birthYear } = this.props.userInfo;
    const dateString = `${birthYear}-${`${+birthMonth}`.padStart(2, '0')}-${`${birthDay}`.padStart(2, '0')}`;
    return this.birthDateEntered() && moment(dateString).isValid();
  }

  private validate18YearsOld(): boolean {
    const { birthDay, birthMonth, birthYear } = this.props.userInfo;
    return validate18YearsOld(+birthDay, +birthMonth - 1, +birthYear);
  }

  private birthDateEntered(): boolean {
    const { birthDay, birthMonth, birthYear } = this.props.userInfo;
    return !!birthDay && !!birthMonth && !!birthYear && birthYear.length === 4;
  }

  private dayEntered(e: any) {
    const value = e.currentTarget.value;
    this.setState({
      enteredDayLength: value.length,
    });
    this.props.userEntersBirthDay(value);
    if (value && value.length === 2) {
      const monthEl = this.monthInput.querySelector('input');
      monthEl.focus();
    }
  }

  private monthEntered(e: any) {
    const value = e.currentTarget.value;
    this.setState({
      enteredMonthLength: value.length,
    });
    this.props.userEntersBirthMonth(value);
    if (value && value.length === 2) {
      const yearEl = this.yearInput.querySelector('input');
      yearEl.focus();
    }
  }

  private yearEntered(e: any) {
    const value = e.currentTarget.value;
    this.setState({
      enteredYearLength: value.length,
    });
    this.props.userEntersBirthYear(value);
  }
}

export default connect(
  (state: IState) =>
    ({
      productInfo: state.productInfo,
      userInfo: state.userInfo,
    }) as IStateProps,
  (dispatch) =>
    bindActionCreators(
      {
        userEntersBirthDay,
        userEntersBirthMonth,
        userEntersBirthYear,
      },
      dispatch,
    ) as IActionProps,
)(BirthDate);
